import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import logo from "../static/media/logo.webp";
import mainLogin from "../static/media/main_login.webp";

const PRIMARY = "#06BBC7";
const BLACK = "#111827";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!form.email.trim() || !form.password.trim()) {
      setError("Ingresa tu correo y contraseña.");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch("/api/auth/login/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: form.email.trim().toLowerCase(),
          password: form.password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || data.error || "No se pudo iniciar sesión.");
      }

      const finalUser = await login({
        access: data.access,
        refresh: data.refresh,
        user: data.user,
      });

      const currentUser = finalUser || data.user || {};
      const requiresPasswordChange =
        data.requires_password_change === true ||
        data.requiere_cambio_clave === true ||
        currentUser.requires_password_change === true ||
        currentUser.requiere_cambio_clave === true;

      if (requiresPasswordChange) {
        navigate("/actualizar-clave", {
          replace: true,
          state: {
            email: currentUser.email || form.email.trim().toLowerCase(),
          },
        });
        return;
      }

      const role = currentUser.role || data.user?.role;

      if (["super_admin", "admin", "staff"].includes(role)) {
        navigate("/admin-panel", { replace: true });
        return;
      }

      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f4f7f8",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: { xs: 2, md: 4 },
        py: 4,
      }}
    >
      <Paper
        component={motion.div}
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 1020,
          minHeight: { xs: "auto", md: 610 },
          borderRadius: { xs: "26px", md: "36px" },
          overflow: "hidden",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "0.88fr 1.12fr" },
          bgcolor: "#ffffff",
          border: "1px solid rgba(17, 24, 39, 0.08)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: { xs: "none", md: "block" },
            bgcolor: BLACK,
            overflow: "hidden",
          }}
        >
          <Box
            component="img"
            src={mainLogin}
            alt="Lily's Sunflowers"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(17,24,39,0.76), rgba(6,187,199,0.26))",
            }}
          />

          <Stack
            spacing={2}
            sx={{
              position: "absolute",
              left: 34,
              right: 34,
              bottom: 34,
              color: "#ffffff",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: PRIMARY,
              }}
            >
              Centro estético integral
            </Typography>

            <Typography
              component="h1"
              sx={{
                fontSize: { md: 34, lg: 40 },
                lineHeight: 1.08,
                fontWeight: 700,
              }}
            >
              Belleza estética con cuidado consciente
            </Typography>

            <Typography
              sx={{
                fontSize: 15,
                lineHeight: 1.7,
                color: "rgba(255,255,255,0.84)",
                maxWidth: 380,
              }}
            >
              Tratamientos faciales, corporales, mesoterapia, peluquería y
              depilación profesional en un espacio pensado para tu bienestar.
            </Typography>
          </Stack>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2.5, sm: 5, md: 7 },
            py: { xs: 4.5, md: 6 },
          }}
        >
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              width: "100%",
              maxWidth: 410,
            }}
          >
            <Stack spacing={2.6}>
              <Stack spacing={2} alignItems={{ xs: "center", md: "flex-start" }}>
                <Box
                  component="img"
                  src={logo}
                  alt="Lily's Sunflowers"
                  sx={{
                    width: { xs: 108, md: 122 },
                    height: "auto",
                    objectFit: "contain",
                  }}
                />

                <Stack spacing={1} sx={{ textAlign: { xs: "center", md: "left" } }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      color: PRIMARY,
                    }}
                  >
                    Bienvenido
                  </Typography>

                  <Typography
                    component="h2"
                    sx={{
                      fontSize: { xs: 30, sm: 36 },
                      lineHeight: 1.12,
                      fontWeight: 700,
                      color: BLACK,
                    }}
                  >
                    Iniciar sesión
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 15,
                      lineHeight: 1.7,
                      color: "#4b5563",
                    }}
                  >
                    Accede a tu cuenta para revisar tu información, seguimiento y
                    servicios disponibles.
                  </Typography>
                </Stack>
              </Stack>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    borderRadius: "14px",
                    fontSize: 14,
                  }}
                >
                  {error}
                </Alert>
              )}

              <Stack spacing={2.2}>
                <TextField
                  fullWidth
                  size="small"
                  name="email"
                  label="Correo"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      bgcolor: "#ffffff",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#4b5563",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#374151",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  size="small"
                  name="password"
                  label="Contraseña"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  disabled={loading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "14px",
                      bgcolor: "#ffffff",
                    },
                    "& .MuiInputLabel-root": {
                      color: "#4b5563",
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                      color: "#374151",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          disabled={loading}
                          sx={{ color: "#4b5563" }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: -1.2,
                }}
              >
                <Link
                  component={RouterLink}
                  to="/recuperar-password"
                  underline="none"
                  sx={{
                    color: PRIMARY,
                    fontSize: 13,
                    fontWeight: 700,
                    "&:hover": {
                      color: "#05a8b3",
                    },
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </Box>

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  height: 46,
                  borderRadius: "14px",
                  bgcolor: PRIMARY,
                  color: "#ffffff",
                  fontSize: 15,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#05a8b3",
                    boxShadow: "none",
                  },
                  "&:disabled": {
                    bgcolor: "rgba(6,187,199,0.45)",
                    color: "#ffffff",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "#ffffff" }} />
                ) : (
                  "Ingresar"
                )}
              </Button>

              <Stack spacing={1.5} alignItems="center">
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: 14,
                    lineHeight: 1.6,
                    color: "#4b5563",
                  }}
                >
                  ¿No tienes una cuenta?{" "}
                  <Link
                    component={RouterLink}
                    to="/registro"
                    underline="none"
                    sx={{
                      color: PRIMARY,
                      fontWeight: 700,
                      "&:hover": {
                        color: "#05a8b3",
                      },
                    }}
                  >
                    Regístrate
                  </Link>
                </Typography>

                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: 12.5,
                    lineHeight: 1.65,
                    color: "#6b7280",
                  }}
                >
                  Al ingresar aceptas nuestras{" "}
                  <Link
                    component={RouterLink}
                    to="/politicas"
                    underline="none"
                    sx={{
                      color: PRIMARY,
                      fontWeight: 700,
                      "&:hover": {
                        color: "#05a8b3",
                      },
                    }}
                  >
                    políticas de privacidad
                  </Link>{" "}
                  y los{" "}
                  <Link
                    component={RouterLink}
                    to="/terminos-condiciones"
                    underline="none"
                    sx={{
                      color: PRIMARY,
                      fontWeight: 700,
                      "&:hover": {
                        color: "#05a8b3",
                      },
                    }}
                  >
                    términos y condiciones
                  </Link>
                  .
                </Typography>

                <Button
                  component={RouterLink}
                  to="/"
                  startIcon={<HomeRoundedIcon />}
                  sx={{
                    color: BLACK,
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13.5,
                    borderRadius: "999px",
                    px: 2,
                    py: 0.8,
                    "&:hover": {
                      bgcolor: "rgba(6,187,199,0.08)",
                      color: PRIMARY,
                    },
                  }}
                >
                  Volver al inicio
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}